export default function () {
    const $config = useRuntimeConfig().public;
    const $route = useRoute();
    const schemaKeywords = useSchemaKeywords();
    const { sanitizeHtml } = useUtilitySanitize();

    function build (lesson) {
        if (!lesson) return undefined;

        function videoSchema () {
            const lessonType = lesson.lesson_category;
            const instrumentType = lesson.lesson_instrument;
            const difficulty = lesson.lesson_difficulty || 'All Levels';

            // Get course information if available
            const courseInfo = lesson.lesson_course || {};

            // Format instrument text correctly (only Ukulele, Electric, Acoustic, or not set)
            function getInstrumentText(instrumentType) {
                if (!instrumentType) return 'Guitar';
                return instrumentType === 'Ukulele' ? 'Ukulele' :
                    instrumentType === 'Electric' ? 'Electric Guitar' :
                        instrumentType === 'Acoustic' ? 'Acoustic Guitar' : 'Guitar';
            }

            const instrumentText = getInstrumentText(instrumentType);

            // Base schema that uses only direct properties
            const baseSchema = {
                '@type': ['VideoObject', 'LearningResource'],
                'url': $config.base + $route.path,
                'name': lesson.lesson_title,
                'uploadDate': lesson.lesson_created,
                'thumbnailUrl': lesson.lesson_image_url,
                'contentUrl': lesson.lesson_video_content_url ? lesson.lesson_video_content_url : undefined,
                'embedUrl': !lesson.lesson_video_content_url && lesson.lesson_video_embed_url ? lesson.lesson_video_embed_url : undefined,
                'description': sanitizeHtml(lesson.lesson_excerpt || lesson.lesson_excerpt_fallback) || 'View this lesson on Andy Guitar',
                'inLanguage': $config.locale || 'en-GB',
                'learningResourceType': 'Concept Overview',
                'educationalUse': 'instruction',
                'educationalLevel': difficulty.toLowerCase(),
                'teaches': {
                    '@type': 'DefinedTerm',
                    'name': lesson.lesson_skill || `${instrumentText} ${lessonType === 'Song' ? 'Performance' : 'Technique'}`,
                },
                'creator': {
                    '@type': 'Person',
                    'name': 'Andy Crowley',
                    'jobTitle': 'Guitar Instructor',
                    'url': $config.base
                },
                'publisher': {
                    '@type': 'Organization',
                    'name': 'Andy Guitar',
                    'url': $config.base
                },
                'timeRequired': 'PT15M',
                'isAccessibleForFree': !lesson.lesson_premium,
                'isPartOf': courseInfo?.course_title ? {
                    '@type': 'Course',
                    'name': courseInfo.course_title,
                    'description': sanitizeHtml(courseInfo.course_excerpt || ''),
                    'provider': {
                        '@type': 'Organization',
                        'name': 'Andy Guitar',
                        'sameAs': $config.base,
                        'member': {
                            '@type': 'Person',
                            'name': 'Andy Crowley',
                            'description': 'Professional guitar instructor with over 15 years of teaching experience'
                        }
                    },
                    'teaches': {
                        '@type': 'DefinedTerm',
                        'name': `${instrumentText} Playing`,
                    },
                    'hasCourseInstance': {
                        '@type': 'CourseInstance',
                        'courseMode': 'Online',
                        'courseWorkload': `PT${(courseInfo.course_lessons?.length || 1) * 15}M`,
                        'instructor': {
                            '@type': 'Person',
                            'name': 'Andy Crowley',
                            'jobTitle': 'Guitar Instructor'
                        },
                        'courseSchedule': {
                            '@type': 'Schedule',
                            'repeatCount': courseInfo.course_lessons?.length || 1,
                            'repeatFrequency': 'Daily'
                        }
                    },
                    'offers': courseInfo.course_archetype === 'free' ? {
                        '@type': 'Offer',
                        'availability': 'https://schema.org/InStock',
                        'price': '0',
                        'priceCurrency': 'GBP',
                        'category': 'Guitar Instruction'
                    } : {
                        '@type': 'Offer',
                        'availability': 'https://schema.org/InStock',
                        'category': 'Guitar Instruction',
                        'priceSpecification': [
                            {
                                '@type': 'PriceSpecification',
                                'price': '10.99',
                                'priceCurrency': 'GBP',
                                'validFrom': new Date().toISOString().split('T')[0],
                                'validThrough': new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
                            },
                        ]
                    },
                    'educationalLevel': difficulty.toLowerCase(),
                    'learningResourceType': 'Video Course',
                    'image': courseInfo.course_image_url,
                    'inLanguage': $config.locale || 'en-GB',
                    'timeRequired': `PT${(courseInfo.course_lessons?.length || 1) * 15}M`,
                    'keywords': schemaKeywords.generateKeywords.course(courseInfo),
                    'educationalAlignment': {
                        '@type': 'AlignmentObject',
                        'alignmentType': 'teaches',
                        'educationalFramework': instrumentType === 'Ukulele' ? 'Ukulele Performance Skills' : `${instrumentText} Performance Skills`,
                        'targetName': `${difficulty} ${instrumentText} Playing`,
                        'targetUrl': `${$config.base}/courses`
                    }
                } : undefined,
                keywords: schemaKeywords.generateKeywords.lesson(lesson),
            };

            // Add specific attributes based on lesson type
            if (lessonType === 'Live Replay') {
                return ({
                    ...baseSchema,
                    'additionalType': 'EducationalVideo',
                    'learningResourceType': 'Concept Overview',
                    'text': lesson.lesson_title
                });
            }

            if (lessonType === 'Song') {
                const title = lesson.lesson_title;
                const titleLower = title.toLowerCase();
                const hasInstrumentInTitle = ['ukulele', 'acoustic', 'electric', 'guitar'].some(term =>
                    titleLower.includes(term)
                );
                const instrumentSuffix = hasInstrumentInTitle ? '' : ` on ${instrumentText.toLowerCase()}`;

                return ({
                    ...baseSchema,
                    'additionalType': 'EducationalVideo',
                    'learningResourceType': 'Problem Walkthrough',
                    'text': titleLower.includes('songs') ?
                        title :
                        `Learn how to play ${title}${instrumentSuffix}`
                });
            }

            if (lessonType === 'Lesson') {
                return ({
                    ...baseSchema,
                    'additionalType': 'EducationalVideo',
                    'learningResourceType': 'Concept Overview',
                    'text': lesson.lesson_excerpt
                });
            }

            return baseSchema;
        }

        const schemaData =
            [
                videoSchema(),
                useSchemaBreadcrumb().buildVideo(lesson, lesson?.lesson_course)
            ];

        return useSchema().build(schemaData);
    }

    return {build};
};
